import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BarChartIcon from '@mui/icons-material/BarChart';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import GroupsIcon from '@mui/icons-material/Groups';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import apiFetch from '../api/apiFetch';
import { getFortnoxConnectionErrorMessage, startFortnoxLogin } from '../auth/session';
import AccountMenu from '../components/AccountMenu';
import TenantPickerForm from '../components/TenantPickerForm';
import controlDeckLogo from '../assets/controldeckLogo.png';

const brandColors = {
  ink: '#10212b',
  muted: '#5f6f7a',
  primary: '#0f766e',
  primaryDark: '#134e4a',
  border: 'rgba(16,33,43,0.09)',
};

export const pageColors = {
  connect: '#0f766e',
  orders: '#388e3c',
  invoices: '#f57c00',
  articles: '#512da8',
  articleStats: '#5e35b1',
  purchaseOrders: '#00695c',
  supplierInvoices: '#455a64',
  summary: '#0097a7',
  customerSales: '#c2185b',
  customerOrderMatrix: '#546e7a',
  crm: '#00796b',
  crmStats: '#00695c',
  customers: '#00897b',
  inventory: '#3949ab',
  statistics: '#6d4c41',
  tenantSettings: '#0277bd',
  users: '#1565c0',
};

const featureDefinitions = [
  {
    title: 'Article Margin Report',
    path: '/articles',
    color: pageColors.articles,
    icon: Inventory2Icon,
  },
  { title: 'Purchase Order', path: '/purchase-orders', color: pageColors.purchaseOrders, icon: ShoppingCartIcon },
  { title: 'Supplier Invoices', path: '/supplier-invoices', color: pageColors.supplierInvoices, icon: RequestQuoteIcon },
  { title: 'Purchase support', path: '/sales-trends', color: pageColors.summary, icon: QueryStatsIcon },
  {
    title: 'Customer Sales Report',
    path: '/customer-order-matrix',
    color: pageColors.customerOrderMatrix,
    icon: ViewWeekIcon,
  },
  {
    title: 'Customer Details',
    path: '/customers',
    color: pageColors.customers,
    icon: PeopleAltOutlinedIcon,
    description: 'Search accounts and open CRM customer details.',
  },
  {
    title: 'Customer Sales Trends',
    path: '/customer-sales-trends',
    color: pageColors.customerSales,
    icon: GroupsIcon,
  },
  {
    title: 'Activity Planner',
    path: '/crm',
    color: pageColors.crm,
    icon: DashboardCustomizeIcon,
    description: 'Review due work and recent CRM activity.',
  },
  {
    title: 'Activity Overview',
    path: '/crm/stats',
    color: pageColors.crmStats,
    icon: BarChartRoundedIcon,
    description: 'Analyze CRM activity counts and matching history.',
    allowedRoles: ['TENANT_ADMIN', 'SUPER_ADMIN'],
  },
  { title: 'Orders', path: '/orders', color: pageColors.orders, icon: AssignmentTurnedInIcon },
  { title: 'Invoices', path: '/invoices', color: pageColors.invoices, icon: ReceiptLongIcon },
  {
    title: 'Financial Report',
    path: '/statistics',
    color: pageColors.statistics,
    icon: BarChartIcon,
  },
];

const baseAdminFeatureDefinitions = [
  {
    title: 'Users',
    path: '/users',
    color: pageColors.users,
    icon: ManageAccountsIcon,
    description: 'Create accounts, adjust roles, and reset passwords.',
  },
];
const superAdminFeatureDefinitions = [
  {
    title: 'Tenants',
    path: '/tenant-settings',
    color: pageColors.tenantSettings,
    icon: SettingsSuggestIcon,
    description: 'Manage tenant records, Fortnox credentials, and CRM enablement.',
  },
];
const mobileVisibleFeaturePaths = new Set(['/articles', '/statistics', '/crm']);
const dashboardLaneDefinitions = [
  {
    title: 'Operations',
    featurePaths: ['/purchase-orders', '/orders'],
  },
  {
    title: 'Reports',
    featurePaths: ['/articles', '/customer-order-matrix', '/sales-trends', '/statistics'],
  },
  {
    title: 'CRM',
    featurePaths: ['/customers', '/customer-sales-trends', '/crm', '/crm/stats'],
  },
];
const crmFeaturePaths = new Set(['/crm', '/crm/stats', '/customers']);
const dashboardCardsGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  },
  gap: 2,
};
const dashboardCardSx = {
  position: 'relative',
  overflow: 'hidden',
  p: { xs: 2.25, md: 2.5 },
  borderRadius: 2,
  textDecoration: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
  minHeight: 148,
};
const sectionHeadingSx = {
  color: brandColors.ink,
  fontWeight: 900,
  letterSpacing: '-0.01em',
};
const shellPaperSx = {
  borderRadius: 2,
  backgroundColor: '#fff',
  border: `1px solid ${brandColors.border}`,
  boxShadow: '0 18px 44px rgba(16,33,43,0.07)',
};

function capitalizeCompanyName(company) {
  const normalizedCompany = company?.trim();

  if (!normalizedCompany) {
    return 'Not available';
  }

  return normalizedCompany.charAt(0).toUpperCase() + normalizedCompany.slice(1);
}

export default function HomePage({
  actingTenant,
  connected,
  crmEnabled,
  currentUser,
  onClearActingTenant,
  onLogout,
  onSetActingTenant,
  setConnected,
  supplierInvoicesEnabled,
  tenantContextRequired,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheStatus, setCacheStatus] = useState('unknown');
  const [connectionFeedback, setConnectionFeedback] = useState(null);
  const [isStartingFortnoxConnect, setIsStartingFortnoxConnect] = useState(false);
  const [isClearingTenant, setIsClearingTenant] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const useCompactAccountMenu = useMediaQuery(theme.breakpoints.down('md'));
  const accountCompanyName = capitalizeCompanyName(currentUser?.company);

  useEffect(() => {
    let cacheStatusInterval;

    const stopPolling = () => {
      if (cacheStatusInterval) {
        clearInterval(cacheStatusInterval);
        cacheStatusInterval = null;
      }
    };

    const clearCachePollingState = (nextStatus = 'unknown') => {
      setCacheStatus(nextStatus);
      setIsRefreshing(false);
    };

    if (tenantContextRequired) {
      setConnectionFeedback(null);
      clearCachePollingState('context-required');
      return undefined;
    }

    const hasConnectedParam = searchParams.get('connected');
    const errorParam = searchParams.get('error');

    if (hasConnectedParam === 'true') {
      setConnectionFeedback({
        severity: 'success',
        message: 'Connection to Fortnox was successful.',
      });
      setConnected(true);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('connected');
      setSearchParams(nextParams, { replace: true });
    } else if (errorParam) {
      setConnectionFeedback({
        severity: 'error',
        message: getFortnoxConnectionErrorMessage(errorParam),
      });
      setConnected(false);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('error');
      setSearchParams(nextParams, { replace: true });
    }

    const checkCacheStatus = async () => {
      try {
        const response = await apiFetch('/api/fortnox/cache-status');

        if (response.status === 401) {
          setConnected(false);
          clearCachePollingState();
          stopPolling();
          return;
        }

        if (response.status === 403) {
          if (currentUser?.role === 'SUPER_ADMIN' && actingTenant == null) {
            clearCachePollingState('context-required');
            stopPolling();
            return;
          }

          setConnectionFeedback({
            severity: 'error',
            message: 'Failed to load Fortnox cache status: access denied.',
          });
          clearCachePollingState();
          stopPolling();
          return;
        }

        if (!response.ok) {
          setConnectionFeedback({
            severity: 'error',
            message: 'Failed to load Fortnox cache status.',
          });
          clearCachePollingState();
          stopPolling();
          return;
        }

        const status = await response.text();
        setCacheStatus(status);
        setIsRefreshing(status === 'refreshing');

        if (status === 'populated') {
          stopPolling();
        }
      } catch (error) {
        clearCachePollingState();
        stopPolling();
        console.error('Error fetching cache status:', error);
        setConnectionFeedback({
          severity: 'error',
          message: error.message || 'Failed to load Fortnox cache status.',
        });
      }
    };

    checkCacheStatus();
    cacheStatusInterval = setInterval(checkCacheStatus, 2000);

    return () => stopPolling();
  }, [
    actingTenant,
    currentUser?.role,
    searchParams,
    setConnected,
    setSearchParams,
    tenantContextRequired,
  ]);

  const handleStartFortnoxConnect = async () => {
    if (tenantContextRequired) {
      return;
    }

    setConnectionFeedback(null);
    setIsStartingFortnoxConnect(true);

    try {
      const authorizationUrl = await startFortnoxLogin();
      window.location.assign(authorizationUrl);
    } catch (error) {
      console.error('Fortnox connect error:', error);

      if (error.status === 403 && currentUser?.role === 'SUPER_ADMIN' && actingTenant == null) {
        return;
      }

      setConnectionFeedback({
        severity: 'error',
        message: error.message,
      });
    } finally {
      setIsStartingFortnoxConnect(false);
    }
  };

  const handleClearTenant = async () => {
    setIsClearingTenant(true);

    try {
      await onClearActingTenant();
    } catch (error) {
      throw error;
    } finally {
      setIsClearingTenant(false);
    }
  };

  const pages = featureDefinitions
    .filter((feature) => supplierInvoicesEnabled || feature.path !== '/supplier-invoices')
    .filter((feature) => crmEnabled || !crmFeaturePaths.has(feature.path))
    .filter((feature) => !feature.allowedRoles || feature.allowedRoles.includes(currentUser?.role))
    .map((feature) => ({
      ...feature,
      disabled: !connected || isRefreshing,
    }));
  const visiblePages = isMobile
    ? pages.filter((feature) => mobileVisibleFeaturePaths.has(feature.path))
    : pages;
  const pagesByPath = new Map(pages.map((feature) => [feature.path, feature]));
  const dashboardLanes = dashboardLaneDefinitions
    .map((lane) => ({
      ...lane,
      features: lane.featurePaths.map((path) => pagesByPath.get(path)).filter(Boolean),
    }))
    .filter((lane) => lane.features.length > 0);

  const formatCacheStatusLabel = () => {
    if (cacheStatus === 'context-required') {
      return 'Tenant required';
    }
    if (isRefreshing) {
      return 'Refreshing...';
    }
    if (cacheStatus === 'populated') {
      return 'Ready';
    }
    if (cacheStatus === 'empty') {
      return 'Empty';
    }
    if (cacheStatus === 'unknown') {
      return 'Unknown';
    }
    return cacheStatus || 'Unknown';
  };

  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
  const canManageUsers = currentUser?.role === 'TENANT_ADMIN' || currentUser?.role === 'SUPER_ADMIN';
  const adminFeatures = isSuperAdmin
    ? [...baseAdminFeatureDefinitions, ...superAdminFeatureDefinitions]
    : baseAdminFeatureDefinitions;

  const renderFeatureCard = (feature) => {
    const IconComponent = feature.icon;
    const cardProps = feature.disabled
      ? {
          component: 'div',
          'aria-disabled': 'true',
        }
      : {
          component: Link,
          to: feature.path,
        };

    return (
      <Paper
        key={feature.path}
        {...cardProps}
        elevation={0}
        sx={{
          ...dashboardCardSx,
          background: feature.disabled
            ? '#f3f5f6'
            : `linear-gradient(145deg, ${feature.color}10 0%, rgba(255,255,255,0.98) 48%, #ffffff 100%)`,
          color: feature.disabled ? 'text.secondary' : brandColors.ink,
          border: `1px solid ${feature.disabled ? 'rgba(16,33,43,0.08)' : `${feature.color}2b`}`,
          boxShadow: feature.disabled ? 'none' : '0 16px 34px rgba(16,33,43,0.07)',
          opacity: feature.disabled ? 0.62 : 1,
          pointerEvents: feature.disabled ? 'none' : 'auto',
          transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '0 0 auto 0',
            height: 4,
            backgroundColor: feature.disabled ? 'rgba(16,33,43,0.18)' : feature.color,
          },
          '&:hover': {
            transform: feature.disabled ? 'none' : 'translateY(-4px)',
            borderColor: feature.disabled ? undefined : `${feature.color}66`,
            boxShadow: feature.disabled ? 'none' : '0 22px 44px rgba(16,33,43,0.12)',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: 2,
            backgroundColor: '#fff',
            color: feature.disabled ? 'text.secondary' : feature.color,
            border: `1px solid ${feature.disabled ? 'rgba(16,33,43,0.1)' : `${feature.color}30`}`,
            boxShadow: feature.disabled ? 'none' : `0 12px 24px ${feature.color}1c`,
          }}
        >
          <IconComponent sx={{ fontSize: 28 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
          {feature.title}
        </Typography>
        <Typography variant="body2" sx={{ color: brandColors.muted, lineHeight: 1.5 }}>
          {feature.description ||
            (feature.path === '/purchase-orders'
            ? 'Track inbound deliveries and supplier fulfillment.'
            : 'Open the detailed dashboard.')}
        </Typography>
      </Paper>
    );
  };

  const renderDashboardLane = (lane, index) => (
    <Box key={`dashboard-lane-${index}`} component="section">
      <Stack spacing={1.75}>
        {lane.title && (
          <Typography variant="h5" sx={sectionHeadingSx}>
            {lane.title}
          </Typography>
        )}
        <Box sx={dashboardCardsGridSx}>{lane.features.map((feature) => renderFeatureCard(feature))}</Box>
      </Stack>
    </Box>
  );

  const renderAdminCards = () => {
    if (!canManageUsers || isMobile) {
      return null;
    }

    return (
      <Box component="section">
        <Stack spacing={1.75}>
          <Typography variant="h5" sx={sectionHeadingSx}>
            Administration
          </Typography>
          <Box sx={dashboardCardsGridSx}>
            {adminFeatures.map((feature) => renderFeatureCard({ ...feature, disabled: false }))}
          </Box>
        </Stack>
      </Box>
    );
  };

  if (tenantContextRequired) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#fff',
          color: brandColors.ink,
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
          <Stack spacing={2.5}>
            <Paper
              elevation={0}
              sx={{
                ...shellPaperSx,
                p: { xs: 1.5, md: 1.75 },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Box
                  component="img"
                  src={controlDeckLogo}
                  alt="ControlDeck"
                  sx={{
                    width: { xs: 170, sm: 240 },
                    maxWidth: 'calc(100% - 52px)',
                    height: { xs: 40, sm: 48 },
                    objectFit: 'contain',
                  }}
                />
                <AccountMenu
                  currentUser={currentUser}
                  onLogout={onLogout}
                  showTenantSettingsLink={isSuperAdmin && useCompactAccountMenu}
                  showCacheStatusLink={isSuperAdmin}
                  cacheStatusDisabled={tenantContextRequired}
                  buttonSx={{
                    flexShrink: 0,
                    borderColor: brandColors.border,
                    px: { xs: 0.5, sm: 1.1 },
                    py: { xs: 0.5, sm: 0.75 },
                    '& .MuiAvatar-root': { width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 } },
                  }}
                />
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                ...shellPaperSx,
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="overline" sx={{ color: brandColors.primary, letterSpacing: 2.5 }}>
                      CONTROLDECK
                    </Typography>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ mt: 0.5, fontWeight: 900 }}>
                      Choose a tenant to continue
                    </Typography>
                  </Box>
                  <Chip label="Tenant context required" color="warning" sx={{ alignSelf: 'flex-start' }} />
                </Stack>
                <Typography variant="body1" sx={{ color: brandColors.muted, maxWidth: 760 }}>
                  You are signed in, but tenant-scoped dashboards and Fortnox actions stay blocked until an acting
                  tenant is selected.
                </Typography>
                <Typography variant="body2" sx={{ color: brandColors.muted }}>
                  Current account company remains <strong>{accountCompanyName}</strong>. The active tenant context is
                  stored separately and is currently not set.
                </Typography>
                <TenantPickerForm
                  currentTenantId={actingTenant?.id ?? null}
                  onSubmit={onSetActingTenant}
                  submitLabel="Use tenant context"
                  helperText="Choose a tenant to unlock tenant-scoped dashboards and Fortnox actions."
                  buttonSx={{
                    backgroundColor: brandColors.primary,
                    '&:hover': { backgroundColor: brandColors.primaryDark },
                  }}
                />
                <Alert severity="info">
                  Tenant-scoped dashboards stay blocked until you choose a tenant, but user management remains
                  available now.
                </Alert>
              </Stack>
            </Paper>

            {renderAdminCards()}
          </Stack>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#fff',
        color: brandColors.ink,
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              ...shellPaperSx,
              p: { xs: 1.5, md: 1.75 },
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Box
                component="img"
                src={controlDeckLogo}
                alt="ControlDeck"
                sx={{
                  width: { xs: 170, sm: 240 },
                  maxWidth: 'calc(100% - 52px)',
                  height: { xs: 40, sm: 48 },
                  objectFit: 'contain',
                }}
              />
              <AccountMenu
                currentUser={currentUser}
                onLogout={onLogout}
                showTenantSettingsLink={isSuperAdmin && useCompactAccountMenu}
                showCacheStatusLink={isSuperAdmin}
                cacheStatusDisabled={tenantContextRequired}
                buttonSx={{
                  flexShrink: 0,
                  borderColor: brandColors.border,
                  px: { xs: 0.5, sm: 1.1 },
                  py: { xs: 0.5, sm: 0.75 },
                  '& .MuiAvatar-root': { width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 } },
                }}
              />
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...shellPaperSx,
              p: { xs: 2.5, md: 3 },
              background: `linear-gradient(145deg, rgba(15,118,110,0.08) 0%, rgba(255,255,255,0.98) 48%, #ffffff 100%)`,
            }}
          >
            <Stack spacing={1}>
              <Typography variant="overline" sx={{ color: brandColors.primary, letterSpacing: 2.5 }}>
                CONTROLDECK
              </Typography>
              <Typography
                component="h1"
                sx={{
                  color: brandColors.ink,
                  fontSize: { xs: '1.85rem', md: '2.35rem' },
                  lineHeight: 1.1,
                  fontWeight: 900,
                }}
              >
                Welcome back{currentUser?.firstName ? `, ${currentUser.firstName}` : ''}
              </Typography>
              <Typography variant="body1" sx={{ color: brandColors.muted, maxWidth: 780 }}>
                Sell smarter, buy better, and protect revenues and margins.
              </Typography>
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ ...shellPaperSx, p: { xs: 1.5, md: 1.75 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {actingTenant && (
                  <Chip
                    label={`Tenant: ${actingTenant.company}`}
                    sx={{ color: brandColors.primary, borderColor: 'rgba(15,118,110,0.32)' }}
                    variant="outlined"
                  />
                )}
                <Chip
                  label={connected ? 'Connected to Fortnox' : 'Not connected'}
                  color={connected ? 'success' : 'default'}
                  variant="filled"
                />
                <Chip
                  label={`Cache: ${formatCacheStatusLabel()}`}
                  color={isRefreshing ? 'warning' : cacheStatus === 'populated' ? 'success' : 'default'}
                  variant="outlined"
                />
              </Stack>
              <Typography
                variant="body1"
                sx={{
                  color: brandColors.ink,
                  fontWeight: 700,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(15,118,110,0.08)',
                  border: '1px solid rgba(15,118,110,0.18)',
                }}
              >
                {actingTenant
                  ? `Working in ${actingTenant.company}`
                  : (
                    <>
                      Company:{' '}
                      <Box component="span" sx={{ color: brandColors.primary, fontWeight: 900 }}>
                        {accountCompanyName}
                      </Box>
                    </>
                  )}
              </Typography>
            </Stack>
          </Paper>

          {isRefreshing && (
            <Alert severity="info">
              Fortnox data refresh in progress. Dashboards are disabled until the sync finishes.
            </Alert>
          )}

          {!connected && (
            <Alert
              severity={connectionFeedback?.severity || 'warning'}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleStartFortnoxConnect}
                  disabled={isStartingFortnoxConnect}
                  sx={{ fontWeight: 800 }}
                >
                  {isStartingFortnoxConnect ? 'Starting...' : 'Connect Fortnox'}
                </Button>
              }
              onClose={connectionFeedback ? () => setConnectionFeedback(null) : undefined}
            >
              {connectionFeedback?.message ||
                'You are logged in. Connect Fortnox manually to enable dashboards and data sync.'}
            </Alert>
          )}

          {connected && connectionFeedback && (
            <Alert severity={connectionFeedback.severity} onClose={() => setConnectionFeedback(null)}>
              {connectionFeedback.message}
            </Alert>
          )}

          {isSuperAdmin && (
            <Paper
              elevation={0}
              sx={{
                ...shellPaperSx,
                p: { xs: 2.5, md: 3 },
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Tenant context
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Switch the active tenant for dashboards and Fortnox actions without leaving the shell.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={actingTenant ? `Active tenant: ${actingTenant.company}` : 'No tenant selected'}
                      color={actingTenant ? undefined : 'warning'}
                      variant="outlined"
                      sx={
                        actingTenant
                          ? { color: brandColors.primary, borderColor: 'rgba(15,118,110,0.32)' }
                          : undefined
                      }
                    />
                    {actingTenant && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleClearTenant}
                        disabled={isClearingTenant}
                        sx={{
                          height: 32,
                          px: 1.75,
                          borderRadius: '999px',
                          textTransform: 'none',
                          backgroundColor: brandColors.primary,
                          '&:hover': { backgroundColor: brandColors.primaryDark },
                        }}
                      >
                        {isClearingTenant ? 'Clearing...' : 'Clear tenant'}
                      </Button>
                    )}
                  </Stack>
                </Stack>

                <TenantPickerForm
                  currentTenantId={actingTenant?.id ?? null}
                  onSubmit={onSetActingTenant}
                  submitLabel="Switch tenant"
                  helperText="Choose the tenant context to use from the dashboard."
                  buttonSx={{
                    backgroundColor: brandColors.primary,
                    '&:hover': { backgroundColor: brandColors.primaryDark },
                  }}
                />
              </Stack>
            </Paper>
          )}

          {isMobile
            ? (
              <Box component="section">
                <Typography variant="h5" sx={{ ...sectionHeadingSx, mb: 1.75 }}>
                  Workspace
                </Typography>
                <Box sx={dashboardCardsGridSx}>{visiblePages.map((feature) => renderFeatureCard(feature))}</Box>
              </Box>
            )
            : dashboardLanes.map((lane, index) => renderDashboardLane(lane, index))}

          {renderAdminCards()}
        </Stack>
      </Container>
    </Box>
  );
}
